import React, { ChangeEvent, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, SortingState, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import { inventoryService, InventoryType } from '../services/inventory.service';
import EditInventoryModal from '../components/Models/EditInventoryModal';
import ViewSummary from '../components/Models/ViewSummary';
import { Inspection } from '../services/inspection.service';

function InventoryPage(): React.ReactElement {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [search, setSearch] = useState<string>('');
    const [data, setData] = useState<InventoryType[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingInventory, setEditingInventory] = useState<InventoryType | null>(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [showViewSummary, setShowViewSummary] = useState(false);
    const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);

    const columnHelper = createColumnHelper<InventoryType>();

    // Initialize data
    useEffect(() => {
        editingInventory === null && getInventory();
    }, [editingInventory]);

    const getInventory = async () => {
        try {
            setIsRefreshing(true)
            setLoading(true);
            setError(null);
            const inventoryData = await inventoryService.get();
            setData(inventoryData ?? [])
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to fetch Inventory List');
        } finally {
            setLoading(false);
            setIsRefreshing(false)
        }
    };

    const handleManualSync = async () => {
        try {
            setIsSyncing(true);
            setError(null);
            await inventoryService.manualSync();
            // After successful sync, refresh the inventory data
            await getInventory();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to synchronize inventory');
        } finally {
            setIsSyncing(false);
        }
    };

    const columns = [
        columnHelper.accessor('trailerId', {
            header: () => <div className="text-left">Trailer ID</div>,
            cell: ({ getValue }) => (
                <div className="text-left">
                    <span className="font-[Geist-Medium] text-black-1">
                        {getValue()}
                    </span>
                </div>
            )
        }),
        columnHelper.accessor('status', {
            header: () => <div className="text-left">Availability</div>,
            cell: ({ getValue }) => (
                <div className="text-left">
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getValue() === 'Entered' ? 'bg-green' : 'bg-red'}`}></div>
                        <span className={`px-3 py-1.5 rounded-full text-xs font-[Geist-SemiBold] border ${getValue() === 'Entered' ? 'bg-green-50 text-green border-green-200' : 'bg-red-50 text-red border-red-200'}`}>
                            {getValue()}
                        </span>
                    </div>
                </div>
            )
        }),
        columnHelper.accessor('entryTime', {
            header: () => <div className="text-left">Entry Time</div>,
            cell: ({ getValue }) => (
                <div className="text-left">
                    <span className="font-[Geist-Medium] text-black-1">
                        {getValue() !== '-' ? format(new Date(getValue()), 'MMM dd, yyyy HH:mm') : getValue()}
                    </span>
                </div>
            )
        }),
        columnHelper.accessor('exitTime', {
            header: () => <div className="text-left">Exit Time</div>,
            cell: ({ getValue }) => (
                <div className="text-left">
                    <span className="font-[Geist-Medium] text-black-1">
                        {getValue() !== '-' ? format(new Date(getValue()), 'MMM dd, yyyy HH:mm') : getValue()}
                    </span>
                </div>
            )
        }),
        columnHelper.display({
            id: 'pastInspections',
            header: () => <div className="text-center">Past Inspections</div>,
            cell: ({ row }) => {
                const { inspection } = row.original;
                
                return (
                    <div className="text-center">
                        <div className="flex items-center">
                            {inspection ? (
                                <button
                                    onClick={() => {
                                        setSelectedInspection(inspection);
                                        setShowViewSummary(true);
                                    }}
                                    className="flex items-center gap-1 bg-blue hover:bg-blue-1 text-white px-3 py-1.5 rounded text-xs font-[Geist-Medium] transition-colors"
                                >
                                    <Icon icon="material-symbols:visibility" className="text-sm" />
                                    View Summary
                                </button>
                            ) : (
                                <span className="text-gray-2 text-xs font-[Geist-Medium]">
                                    Not Found
                                </span>
                            )}
                        </div>
                    </div>
                );
            }
        }),
        columnHelper.display({
            id: 'actions',
            header: () => <div className="text-center">Action</div>,
            cell: ({ row }) => {
                const { url, trailerId } = row.original;
                const canEdit = url !== '' && trailerId === 'Unidentified';

                return (
                    <div className="text-center">
                        <div className="flex items-center">
                            {canEdit ? (
                                <button
                                    onClick={() => setEditingInventory(row.original)}
                                    className="flex items-center justify-center w-8 h-8 rounded-full bg-green hover:bg-green-600 text-white transition-colors"
                                    title="Edit"
                                >
                                    <Icon icon="lucide:edit-3" className="text-lg" />
                                </button>
                            ) : (
                                <span className="font-[Geist-Medium] text-gray-2 text-sm">
                                    No Action
                                </span>
                            )}
                        </div>
                    </div>
                );
            }
        })
    ];

    const table = useReactTable({
        data: data,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        getFilteredRowModel: getFilteredRowModel(),
        onGlobalFilterChange: setSearch,
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 6,
            }
        },
        state: {
            sorting: sorting,
            globalFilter: search
        }
    });

    if (loading) {
        return (
            <div className="w-full h-full bg-white rounded-xl border border-gray-3 flex items-center justify-center">
                <div className="text-gray-2">Loading Inventory List...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full h-full bg-white rounded-xl border border-gray-3 flex items-center justify-center">
                <div className="text-red-500">
                    {error}
                    <button onClick={getInventory} className="ml-2 underline">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full bg-white rounded-xl border border-gray-3 flex flex-col gap-6">
            {editingInventory && <EditInventoryModal visible={editingInventory} setVisible={setEditingInventory} />}
            {showViewSummary && <ViewSummary onClose={() => setShowViewSummary(false)} inspection={selectedInspection} />}
            {/* Header Section */}
            <div className="w-full flex flex-col gap-3 p-4 pl-8">
                <div className="w-full flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <h2 className="font-[Geist-Bold] text-lg md:text-xl text-green">
                            Inventory Management
                        </h2>
                        <div className="relative group">
                            <div className="w-6 h-6 rounded-full bg-green-1 border border-green-200 flex items-center justify-center cursor-help hover:bg-green-200 transition-colors duration-200">
                                <Icon
                                    icon="material-symbols:help-outline"
                                    className="text-green text-base"
                                />
                            </div>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-2 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50">
                                Auto-updates every 3 minutes
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-b-2 border-transparent border-b-gray-2"></div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleManualSync}
                            disabled={isSyncing || isRefreshing}
                            className="flex items-center gap-2 bg-blue hover:bg-blue-1 text-white px-4 py-2 rounded transition-colors disabled:opacity-50"
                        >
                            <Icon
                                icon={isSyncing ? "eos-icons:loading" : "material-symbols:sync"}
                                className={`text-lg ${isSyncing ? 'animate-spin' : ''}`}
                            />
                            <span className="font-[Geist-Medium] text-sm">
                                {isSyncing ? 'Synchronizing...' : 'Synchronize Now'}
                            </span>
                        </button>
                        <button
                            onClick={getInventory}
                            disabled={isRefreshing}
                            className="flex items-center gap-2 bg-green hover:bg-green-600 text-white px-4 py-2 rounded transition-colors disabled:opacity-50"
                        >
                            <Icon
                                icon={isRefreshing ? "eos-icons:loading" : "material-symbols:refresh"}
                                className={`text-lg ${isRefreshing ? 'animate-spin' : ''}`}
                            />
                            <span className="font-[Geist-Medium] text-sm">
                                {isRefreshing ? 'Refreshing...' : 'Refresh'}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Search Input and Total Count */}
                <div className="flex items-center justify-between">
                    <div className="relative">
                        <input
                            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
                            value={search}
                            type="text"
                            className="w-full max-w-sm border border-gray-3 rounded px-4 py-2 pl-10 focus:outline-none focus:border-green font-[Geist-Medium]"
                            placeholder="Search inventory..."
                        />
                        <Icon
                            icon="ri:search-line"
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-2 text-xl"
                        />
                    </div>
                    <div className="flex items-center gap-2 bg-green-1 border border-gray-3 rounded-lg px-3 py-1.5 hover:bg-light-green transition-colors duration-200">
                        <div className="w-2 h-2 bg-green rounded-full"></div>
                        <span className="text-xs text-gray font-[Geist-Medium]">Total Available:</span>
                        <span className="text-sm text-green font-[Geist-Bold]">
                            {data.filter(item => item.status === 'Entered').length}
                        </span>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="w-full px-3 flex-1 overflow-hidden flex flex-col">
                {data.length > 0 ? (
                    <div className="w-full border-t border-gray-3 h-full flex flex-col">
                        <div className="flex-1 overflow-y-auto" style={{
                            maxHeight: 'calc(100vh - 400px)',
                            minHeight: '300px'
                        }}>
                            <table className="w-full table-fixed">
                                <thead className="sticky top-0 bg-white z-10">
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id} className="h-16">
                                            {headerGroup.headers.map(header => (
                                                <th
                                                    key={header.id}
                                                    className="px-4 text-gray font-[Geist-Medium] text-xs md:text-sm bg-white"
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <div
                                                            onClick={header.column.getToggleSortingHandler()}
                                                            className="flex items-center cursor-pointer hover:text-green transition-colors"
                                                        >
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                            {header.column.getIsSorted() ? (
                                                                header.column.getIsSorted() === 'asc' ? (
                                                                    <Icon icon="solar:square-alt-arrow-up-bold" className="text-lg ml-1" />
                                                                ) : (
                                                                    <Icon icon="solar:square-alt-arrow-down-bold" className="text-lg ml-1" />
                                                                )
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row, index) => (
                                        <tr
                                            key={row.id}
                                            className={`h-16 border-t border-gray-3 text-gray-2 font-[Geist-Medium] text-xs md:text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-6'
                                                }`}
                                        >
                                            {row.getVisibleCells().map((cell: any) => (
                                                <td key={cell.id} className="px-4">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="w-full rounded-xl h-40 flex justify-center items-center bg-white">
                        <span className="font-[Geist-Medium] text-sm text-gray-2">
                            No Data Found
                        </span>
                    </div>
                )}
            </div>

            {/* Pagination */}
            <div className="w-full flex justify-between items-center p-5 border-t border-gray-3">
                <div className="text-sm text-gray-2 font-[Geist-Medium]">
                    Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                    {Math.min(
                        (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                        table.getFilteredRowModel().rows.length
                    )}{' '}
                    of {table.getFilteredRowModel().rows.length} entries
                </div>

                {table.getPageCount() > 1 && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            className={`flex items-center ${table.getCanPreviousPage()
                                ? 'text-green hover:text-green-600 cursor-pointer'
                                : 'text-gray-2 cursor-default'
                                }`}
                        >
                            <Icon icon="ic:sharp-chevron-left" className="text-3xl" />
                        </button>

                        <div className="flex items-center gap-2">
                            {[...Array(table.getPageCount())].map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => table.setPageIndex(index)}
                                    className={`w-9 h-9 flex justify-center items-center rounded-full font-[Geist-Medium] cursor-pointer transition-colors ${table.getState().pagination.pageIndex === index
                                        ? 'bg-green text-white'
                                        : 'bg-gray-3 text-gray-2 hover:bg-gray-2'
                                        }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            className={`flex items-center ${table.getCanNextPage()
                                ? 'text-green hover:text-green-600 cursor-pointer'
                                : 'text-gray-2 cursor-default'
                                }`}
                        >
                            <Icon icon="ic:sharp-chevron-right" className="text-3xl" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default InventoryPage;
